/**
 * AgentExecutionPanel Component
 *
 * Displays the list of selected agents and their execution status
 */

import React from 'react';
import {
  SpecialistAgentType,
  AgentResult,
  ExecutionStatus,
  AGENT_METADATA
} from '../../types/superAgent';

interface AgentExecutionPanelProps {
  selectedAgents: SpecialistAgentType[];
  executionStatus: ExecutionStatus;
  agentResults: AgentResult[];
}

// Status badge component
const StatusBadge: React.FC<{ status: ExecutionStatus | 'pending' | 'running' | 'completed' | 'failed' }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'analyzing':
        return { color: 'bg-blue-100 text-blue-800', label: 'Analyzing', icon: '🔍' };
      case 'executing':
      case 'running':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Running', icon: '⚙️' };
      case 'aggregating':
        return { color: 'bg-purple-100 text-purple-800', label: 'Aggregating', icon: '🔄' };
      case 'completed':
        return { color: 'bg-green-100 text-green-800', label: 'Completed', icon: '✅' };
      case 'failed':
        return { color: 'bg-red-100 text-red-800', label: 'Failed', icon: '❌' };
      case 'pending':
        return { color: 'bg-gray-100 text-gray-800', label: 'Pending', icon: '⏳' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: status, icon: '•' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <span className="mr-1">{config.icon}</span>
      {config.label}
    </span>
  );
};

// Individual agent status item
const AgentStatusItem: React.FC<{
  agentType: SpecialistAgentType;
  result?: AgentResult;
  globalStatus: ExecutionStatus;
}> = ({ agentType, result, globalStatus }) => {
  const metadata = AGENT_METADATA[agentType];

  // Determine agent status
  const getAgentStatus = () => {
    if (result) {
      return result.status;
    }
    // If no result yet, infer from global status
    if (globalStatus === 'pending') {
      return 'pending';
    }
    if (globalStatus === 'running') {
      return 'running';
    }
    return 'pending';
  };

  const agentStatus = getAgentStatus();

  return (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center space-x-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
          style={{ backgroundColor: metadata.color + '20' }}
        >
          {metadata.icon}
        </div>
        <div>
          <div className="font-medium text-gray-900">{metadata.name}</div>
          <div className="text-xs text-gray-500">{metadata.description}</div>
        </div>
      </div>
      <div>
        <StatusBadge status={agentStatus} />
      </div>
    </div>
  );
};

const AgentExecutionPanel: React.FC<AgentExecutionPanelProps> = ({
  selectedAgents,
  executionStatus,
  agentResults
}) => {
  // Calculate progress
  const completedCount = agentResults.filter(r => r.status === 'completed' || r.status === 'failed').length;
  const successCount = agentResults.filter(r => r.status === 'completed').length;
  const failedCount = agentResults.filter(r => r.status === 'failed').length;
  const totalCount = selectedAgents.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const successRate = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

  // Format execution time
  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}秒`;
  };

  // Calculate total execution time from results
  const totalTime = agentResults.reduce((sum, r) => sum + (r.execution_time_ms || 0), 0);

  return (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          エージェント実行状況
        </h3>
        <StatusBadge status={executionStatus} />
      </div>

      {/* Progress Bar */}
      {executionStatus !== 'completed' && executionStatus !== 'failed' && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>進捗</span>
            <span>{completedCount} / {totalCount}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Agent List */}
      <div className="space-y-2">
        {selectedAgents.map((agentType, index) => {
          const result = agentResults.find(r => r.agent_type === agentType);
          return (
            <AgentStatusItem
              key={`${agentType}-${index}`}
              agentType={agentType}
              result={result}
              globalStatus={executionStatus}
            />
          );
        })}
      </div>

      {/* Summary Stats */}
      {executionStatus === 'completed' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-4 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {successCount}
              </div>
              <div className="text-xs text-gray-600">成功</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {failedCount}
              </div>
              <div className="text-xs text-gray-600">失敗</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(successRate)}%
              </div>
              <div className="text-xs text-gray-600">成功率</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {formatTime(totalTime)}
              </div>
              <div className="text-xs text-gray-600">合計時間</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentExecutionPanel;
